import { Component, computed, input, output, resource, signal } from '@angular/core';
import { form, FormField } from '@angular/forms/signals';
import { provideTranslocoScope, TranslocoDirective, TranslocoPipe } from '@jsverse/transloco';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { lucideCheck, lucideCopy, lucideLoader } from '@ng-icons/lucide';
import { BrnInputOtp } from '@spartan-ng/brain/input-otp';
import { HlmButtonImports } from '@spartan-ng/helm/button';
import { HlmFieldImports } from '@spartan-ng/helm/field';
import { HlmInputOtpImports } from '@spartan-ng/helm/input-otp';
import { toDataURL } from 'qrcode';

const CODE_PATTERN = /^\d{6}$/;

/**
 * Presentational two-factor enrollment, shared by the login (forced enrollment) and the profile
 * (self-service activation). It never talks to the backend: the parent loads the secret, checks
 * the code and, once the server accepts it, passes the one-time `recoveryCodes` back in.
 *
 * Two views: the QR + code form, and — when `recoveryCodes` is set — the codes, shown once.
 * `error` is a translation key (the parent owns the vocabulary: `auth.signIn.errors.*` or
 * `iam.error.*`).
 */
@Component({
  selector: 'au-two-factor-enrollment',
  imports: [
    FormField,
    TranslocoDirective,
    TranslocoPipe,
    NgIcon,
    HlmButtonImports,
    HlmFieldImports,
    BrnInputOtp,
    HlmInputOtpImports,
  ],
  providers: [
    provideTranslocoScope('auth'),
    provideIcons({ lucideCheck, lucideCopy, lucideLoader }),
  ],
  template: `
    <div
      *transloco="let t; scope: 'auth'"
      class="space-y-4"
    >
      @if (recoveryCodes(); as codes) {
        <div class="space-y-1">
          <h3 class="text-base font-medium">{{ t('auth.twoFactor.RecoveryCodesTitle') }}</h3>
          <p class="text-muted-foreground text-sm">
            {{ t('auth.twoFactor.RecoveryCodesWarning') }}
          </p>
        </div>
        <ul class="bg-muted grid grid-cols-2 gap-x-4 gap-y-1 rounded-md p-4 font-mono text-sm">
          @for (code of codes; track code) {
            <li data-testid="recovery-code">{{ code }}</li>
          }
        </ul>
        <div class="flex flex-wrap justify-end gap-2">
          <button
            data-testid="copy-codes"
            type="button"
            variant="outline"
            hlmBtn
            (click)="copyCodes(codes)"
          >
            <ng-icon
              aria-hidden="true"
              [name]="copyState() === 'copied' ? 'lucideCheck' : 'lucideCopy'"
            />
            @switch (copyState()) {
              @case ('copied') {
                {{ t('auth.twoFactor.Copied') }}
              }
              @case ('failed') {
                {{ t('auth.twoFactor.CopyFailed') }}
              }
              @default {
                {{ t('auth.twoFactor.CopyCodes') }}
              }
            }
          </button>
          <button
            data-testid="done"
            type="button"
            hlmBtn
            (click)="done.emit()"
          >
            {{ t('auth.twoFactor.Done') }}
          </button>
        </div>
      } @else {
        <p class="text-muted-foreground text-sm">{{ t('auth.twoFactor.ScanInstruction') }}</p>
        <div class="flex justify-center">
          <!-- White backdrop in every theme: a QR inverted by dark mode does not scan. -->
          @if (qrDataUrl(); as src) {
            <img
              data-testid="qr-image"
              class="size-44 rounded-md bg-white p-2"
              [src]="src"
              [alt]="t('auth.twoFactor.QrAlt', { secret: secret() })"
            />
          } @else {
            <div
              aria-hidden="true"
              class="bg-muted size-44 animate-pulse rounded-md"
            ></div>
          }
        </div>
        <div class="text-center text-sm">
          <span class="text-muted-foreground">{{ t('auth.twoFactor.ManualKey') }}</span>
          <code
            data-testid="secret"
            class="mt-1 block font-mono tracking-wider break-all"
          >
            {{ secret() }}
          </code>
        </div>

        <form
          novalidate
          (submit)="$event.preventDefault(); submitCode()"
        >
          <hlm-field-group>
            <hlm-field class="items-center text-center">
              <label
                class="justify-center"
                hlmFieldLabel
              >
                {{ t('auth.twoFactor.CodeLabel') }}
              </label>
              <div class="flex justify-center">
                <brn-input-otp
                  data-testid="code-input"
                  [formField]="codeForm.code"
                  [transformPaste]="digitsOnly"
                  hlmInputOtp
                  length="6"
                >
                  <hlm-input-otp-group>
                    @for (index of firstHalf; track index) {
                      <hlm-input-otp-slot [index]="index" />
                    }
                  </hlm-input-otp-group>
                  <hlm-input-otp-separator />
                  <hlm-input-otp-group>
                    @for (index of secondHalf; track index) {
                      <hlm-input-otp-slot [index]="index" />
                    }
                  </hlm-input-otp-group>
                </brn-input-otp>
              </div>
              @if (error(); as key) {
                <hlm-field-error
                  data-testid="error"
                  forceShow
                >
                  {{ key | transloco }}
                </hlm-field-error>
              } @else if (invalidFormat()) {
                <hlm-field-error forceShow>{{ t('auth.twoFactor.CodeInvalid') }}</hlm-field-error>
              }
            </hlm-field>
            <hlm-field>
              <button
                data-testid="submit"
                type="submit"
                [attr.aria-busy]="loading() ? 'true' : null"
                [disabled]="loading()"
                hlmBtn
              >
                @if (loading()) {
                  <ng-icon
                    name="lucideLoader"
                    aria-hidden="true"
                    class="animate-spin text-base"
                  />
                }
                {{ t('auth.twoFactor.Submit') }}
              </button>
            </hlm-field>
          </hlm-field-group>
        </form>
      }
    </div>
  `,
})
export class AuTwoFactorEnrollment {
  // inputs
  readonly otpauthUri = input<string | null>(null);
  readonly secret = input<string>('');
  readonly recoveryCodes = input<string[] | null>(null);
  /** Translation key of the message to show under the code input. */
  readonly error = input<string | null>(null);
  readonly loading = input(false);

  // outputs
  readonly codeSubmitted = output<string>();
  /** The user confirmed having saved the recovery codes. */
  readonly done = output<void>();

  // state
  protected readonly copyState = signal<'idle' | 'copied' | 'failed'>('idle');
  private readonly submitted = signal(false);

  // Spartan's OTP input has no built-in separator: the 3 + 3 split is laid out here.
  protected readonly firstHalf = [0, 1, 2];
  protected readonly secondHalf = [3, 4, 5];
  /** Pasted codes often carry spaces or dashes ("123 456"); only the digits count. */
  protected readonly digitsOnly = (text: string): string => text.replace(/\D/g, '');

  protected readonly model = signal({ code: '' });
  protected readonly codeForm = form(this.model);
  protected readonly invalidFormat = computed(
    () => this.submitted() && !CODE_PATTERN.test(this.model().code),
  );

  private readonly qr = resource({
    params: () => this.otpauthUri(),
    loader: ({ params }) =>
      params ? toDataURL(params, { margin: 1, width: 176 }) : Promise.resolve(null),
  });
  protected readonly qrDataUrl = computed(() => (this.qr.hasValue() ? this.qr.value() : null));

  protected submitCode(): void {
    this.submitted.set(true);
    const { code } = this.model();
    if (CODE_PATTERN.test(code)) this.codeSubmitted.emit(code);
  }

  protected async copyCodes(codes: string[]): Promise<void> {
    try {
      await navigator.clipboard.writeText(codes.join('\n'));
      this.copyState.set('copied');
    } catch {
      // The codes stay on screen: the user can still select them by hand.
      this.copyState.set('failed');
    }
  }
}
